import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import CameraLoader from "./components/CameraLoader";
import API_BASE_URL from "./config";
import "./QrSharing.css";
import { clearAuthStorage } from "./auth/logout";

function QrSharing() {
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [formData, setFormData] = useState({ name: "", eventType: "", place: "", eventDate: "" });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [editingId, setEditingId] = useState(null);
  const userId = localStorage.getItem("userId");

  useEffect(() => { fetchGalleries(); }, []);
  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(""), 4000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(""), 4000); return () => clearTimeout(t); } }, [error]);

  const fetchGalleries = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr-galleries/${userId}`);
      const data = await res.json();
      setGalleries(data.galleries || []);
    } catch { setError("Failed to fetch galleries"); }
    finally { setFetchLoading(false); }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    if (!formData.name) { setError("Gallery name is required"); setLoading(false); return; }

    try {
      if (editingId) {
        const res = await fetch(`${API_BASE_URL}/api/qr-galleries/${editingId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        if (!res.ok) throw new Error("Failed to update gallery");
        setSuccess("Gallery updated!"); setEditingId(null);
      } else {
        const res = await fetch(`${API_BASE_URL}/api/qr-galleries`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, ...formData }),
        });
        if (!res.ok) throw new Error("Failed to create gallery");
        setSuccess("QR Gallery created! Click on it to add photos.");
      }
      setFormData({ name: "", eventType: "", place: "", eventDate: "" });
      await fetchGalleries();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this QR gallery and all its photos?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr-galleries/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete");
      setSuccess("Gallery deleted!"); await fetchGalleries();
    } catch (err) { setError(err.message); }
  };

  const handleEdit = (gallery, e) => {
    e.stopPropagation();
    setFormData({
      name: gallery.name, eventType: gallery.event_type || "",
      place: gallery.place || "", eventDate: gallery.event_date ? gallery.event_date.split("T")[0] : "",
    });
    setEditingId(gallery.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ name: "", eventType: "", place: "", eventDate: "" });
  };

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login");
  };

  return (
    <div className="qrs-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="qrs-main-content">
        <header className="qrs-header">
          <div className="qrs-header-left">
            <button className="qrs-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="qrs-page-title">QR Code Sharing</h1>
          </div>
          <div className="qrs-header-right">
            <button className="qrs-logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </header>

        <main className="qrs-main">
          <div className="qrs-content-wrapper">
            {/* Form */}
            <div className="qrs-form-section">
              <div className="qrs-form-card">
                <h2>{editingId ? "✏️ Edit QR Gallery" : "📱 Create QR Gallery"}</h2>
                <form onSubmit={handleSubmit} className="qrs-form">
                  <div className="qrs-form-grid">
                    <div className="qrs-form-group">
                      <label>Gallery Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleFormChange} placeholder="e.g., Wedding Reception" required />
                    </div>
                    <div className="qrs-form-group">
                      <label>Event Type</label>
                      <input type="text" name="eventType" value={formData.eventType} onChange={handleFormChange} placeholder="e.g., Wedding, Birthday" />
                    </div>
                    <div className="qrs-form-group">
                      <label>Place</label>
                      <input type="text" name="place" value={formData.place} onChange={handleFormChange} placeholder="e.g., Grand Ballroom" />
                    </div>
                    <div className="qrs-form-group">
                      <label>Event Date</label>
                      <input type="date" name="eventDate" value={formData.eventDate} onChange={handleFormChange} />
                    </div>
                  </div>

                  {error && <div className="qrs-error-message">{error}</div>}
                  {success && <div className="qrs-success-message">{success}</div>}

                  <div className="qrs-form-buttons">
                    <button type="submit" disabled={loading} className="qrs-btn-submit">
                      {loading ? "Processing..." : editingId ? "Update Gallery" : "Create Gallery"}
                    </button>
                    {editingId && <button type="button" onClick={handleCancel} className="qrs-btn-cancel">Cancel</button>}
                  </div>
                </form>
              </div>
            </div>

            {/* Gallery List */}
            <div className="qrs-list-section">
              <div className="qrs-list-header"><h2>📋 Your QR Galleries ({galleries.length})</h2></div>
              {fetchLoading ? <CameraLoader text="Loading galleries..." /> : galleries.length === 0 ? (
                <div className="qrs-empty-state">
                  <p className="qrs-empty-icon">📱</p>
                  <p className="qrs-empty-text">No QR galleries yet. Create your first one!</p>
                </div>
              ) : (
                <div className="qrs-grid">
                  {galleries.map((g) => (
                    <div key={g.id} className="qrs-card" onClick={() => navigate(`/qr-sharing/${g.id}`)} style={{ cursor: "pointer" }}>
                      <div className="qrs-card-header">
                        <h3>{g.name}</h3>
                        <span className="qrs-card-date">{g.event_date ? new Date(g.event_date).toLocaleDateString() : "No date"}</span>
                      </div>
                      {g.event_type && <div className="qrs-card-type-tag">{g.event_type}</div>}
                      <div className="qrs-card-body">
                        {g.place && <div className="qrs-card-item"><span className="qrs-card-label">📍 Place:</span><span className="qrs-card-value">{g.place}</span></div>}
                        <div className="qrs-card-item"><span className="qrs-card-label">📸 Photos:</span><span className="qrs-card-value">{g.image_count || 0}</span></div>
                      </div>
                      <div className="qrs-card-footer">
                        <span className="qrs-card-action-hint">Manage Photos & QR →</span>
                        <div className="qrs-card-btns">
                          <button className="qrs-card-edit-btn" onClick={(e) => handleEdit(g, e)} title="Edit">✏️</button>
                          <button className="qrs-card-delete-btn" onClick={(e) => handleDelete(g.id, e)} title="Delete">🗑</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default QrSharing;
